import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Shepherd from 'shepherd.js';
import 'shepherd.js/dist/css/shepherd.css';

import CorrectSlide from '../../../assets/correct-slide.png';
import CorrectValve from '../../../assets/correct-valve.png';
import EmptyCorrectValve from '../../../assets/empty-correct-valve.png';
import EmptyHeart from '../../../assets/empty-heart.png';
import Heart from '../../../assets/heart.png';
import HelpButtonImage from '../../../assets/help-button.png';
import IncorrectValve from '../../../assets/incorrect-valve.png';
import optionsButtonImage from '../../../assets/options-button.png';
import BackButton from '../../../components/BackButton/BackButton';
import Instrument from '../../../components/Instrument/Instrument';
import Loading from '../../../components/Loading/Loading';
import MusicNote from '../../../components/MusicNote/MusicNote';
import Options from '../../../components/Options/Options';
import { brassDict } from '../../../utils/Dictionaries/brassDict';
import { notes } from '../../../utils/Dictionaries/brassNotes';
import { notesWithoutFlats } from '../../../utils/Dictionaries/brassNotesWithoutFlats';
import { keySignatures } from '../../../utils/Dictionaries/keySignatures';
import Styles from './Classic.module.css';

const Classic = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [lives, setLives] = useState(3);
  const [score, setScore] = useState(0);
  const [isTimeUp, setIsTimeUp] = useState(false);
  const [showOptions, setShowOptions] = useState(false);

  const navigate = useNavigate();

  // Load options from storage — return storedData as-is (matches Sandbox
  // and Options.jsx). Previously this hardcoded slider1Value/slider2Value,
  // which silently discarded whatever range the user picked in Options.
  const loadOptions = () => {
    const storedData = JSON.parse(localStorage.getItem('options'));
    if (storedData) {
      return storedData;
    }
    return {
      selectedInstrument: 'trumpet',
      selectedRange: 'custom',
      slider1Value: 0,
      slider2Value: notesWithoutFlats['trumpet'].length - 1,
      keyLetter: 'none',
      accidental: 'natural',
      mode: 'major',
      volume: 50,
    };
  };

  const [selectedInstrument, setSelectedInstrument] = useState(
    loadOptions().selectedInstrument,
  );
  const [slider1Value, setSlider1Value] = useState(loadOptions().slider1Value);
  const [slider2Value, setSlider2Value] = useState(loadOptions().slider2Value);
  const [keyLetter, setKeyLetter] = useState(loadOptions().keyLetter);
  const [accidental, setAccidental] = useState(loadOptions().accidental);
  const [mode, setMode] = useState(loadOptions().mode);
  const [volume, setVolume] = useState(loadOptions().volume);

  // Timer
  const [timer, setTimer] = useState(30);
  const [seconds, setSeconds] = useState(30);
  const [isRunning, setIsRunning] = useState(true);
  const timerId = useRef(null);

  useEffect(() => {
    if (isRunning) {
      timerId.current = setInterval(() => {
        setTimer((prevTimer) => {
          if (prevTimer <= 1) {
            clearInterval(timerId.current);
            setIsRunning(false);
            setIsTimeUp(true);
            return 0;
          }
          return prevTimer - 1;
        });
      }, 1000);
    } else {
      clearInterval(timerId.current);
    }

    return () => clearInterval(timerId.current);
  }, [isRunning]);

  const resetTimer = () => {
    setTimer(seconds);
    setIsRunning(true);
  };

  // Handle random note
  const getRandomInt = (min, max, instr = selectedInstrument) => {
    if (max < min) {
      let temp = min;
      min = max;
      max = temp;
    }
    min = notes[instr].indexOf(notesWithoutFlats[instr][min]);
    max = notes[instr].indexOf(notesWithoutFlats[instr][max]);

    if (min === -1) {
      min = 0;
    }
    if (max === -1) {
      max = notes[instr].length - 1;
    }

    if (notes[instr][max].includes('#')) max++;
    return Math.floor(Math.random() * (max - min + 1)) + min;
  };

  // Check if a note is excluded (Cb, Fb, B#, E#) — same as Sandbox.
  // Previously missing entirely, so Classic could land on these.
  const isExcludedNote = (note) => {
    const noteWithoutOctave = note.substring(0, note.length - 1);
    return ['Cb', 'Fb', 'B#', 'E#'].includes(noteWithoutOctave);
  };

  const getInitialNoteIndex = () => {
    let randomInt = getRandomInt(slider1Value, slider2Value);
    let attempts = 0;
    const maxAttempts = 100;
    while (
      isExcludedNote(notes[selectedInstrument][randomInt]) &&
      attempts < maxAttempts
    ) {
      randomInt = getRandomInt(slider1Value, slider2Value);
      attempts++;
    }
    return randomInt;
  };

  const [noteIndex, setNoteIndex] = useState(getInitialNoteIndex());

  // Now filters by key signature and skips excluded notes, matching
  // Sandbox. Reads options fresh from localStorage (not closured state)
  // to avoid acting on a stale value right after the Options panel closes.
  const changeNote = () => {
    const opts = loadOptions();
    const si = opts.selectedInstrument;
    let s1 = opts.slider1Value;
    let s2 = opts.slider2Value;

    let randomInt = getRandomInt(s1, s2, si);
    let currentNote = notes[si][randomInt];
    currentNote = currentNote.substring(0, currentNote.length - 1);
    let keySignature = opts.keyLetter;
    if (opts.keyLetter !== 'none') {
      if (opts.accidental === 'sharp') keySignature += '#';
      else if (opts.accidental === 'flat') keySignature += 'b';
      if (opts.mode === 'minor') keySignature += 'm';
    }
    let attempts = 0;
    const maxAttempts = 100;
    while (attempts < maxAttempts) {
      if (isExcludedNote(notes[si][randomInt])) {
        randomInt = getRandomInt(s1, s2, si);
        currentNote = notes[si][randomInt];
        currentNote = currentNote.substring(0, currentNote.length - 1);
      } else if (opts.keyLetter !== 'none' && keySignature in keySignatures) {
        if (!keySignatures[keySignature].includes(currentNote)) {
          randomInt = getRandomInt(s1, s2, si);
          currentNote = notes[si][randomInt];
          currentNote = currentNote.substring(0, currentNote.length - 1);
        } else {
          break;
        }
      } else {
        break;
      }
      attempts++;
    }
    setNoteIndex(randomInt);
  };

  // Reset the round whenever options change, same as Sandbox
  useEffect(() => {
    setLives(3);
    setScore(0);
    setSeconds(30);
    setTimer(30);
    setIsRunning(true);
    setIsTimeUp(false);
    changeNote();
  }, [selectedInstrument, slider1Value, slider2Value, keyLetter, accidental, mode]);

  // Update localResults
  useEffect(() => {
    const updateResults = async () => {
      const resultsData = {
        score,
      };
      localStorage.setItem('localResults', JSON.stringify(resultsData));

      navigate('/classroom/classic/results');
    };

    if (lives === 0) {
      updateResults();
    }
  }, [lives]);

  const [showCorrectValve1, setShowCorrectValve1] = useState(null);
  const [showCorrectValve2, setShowCorrectValve2] = useState(null);
  const [showCorrectValve3, setShowCorrectValve3] = useState(null);
  const [showCorrectValve4, setShowCorrectValve4] = useState(null);
  const [showCorrectTrigger, setShowCorrectTrigger] = useState(null);
  const [showCorrectSlide, setShowCorrectSlide] = useState(null);
  const [correctSlideDistance, setCorrectSlideDistance] = useState(0);
  const correctSlideNumber = Number(
    brassDict[selectedInstrument][notes[selectedInstrument][noteIndex]][0],
  );

  // Feedback overlay
  const [feedbackType, setFeedbackType] = useState(null);
  const feedbackTimerRef = useRef(null);

  const showFeedback = (type, duration = 300) => {
    setFeedbackType(type);
    if (feedbackTimerRef.current) {
      clearTimeout(feedbackTimerRef.current);
    }
    feedbackTimerRef.current = setTimeout(() => {
      setFeedbackType(null);
      feedbackTimerRef.current = null;
    }, duration);
  };

  useEffect(() => {
    return () => {
      if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
    };
  }, []);

  const handleIncorrect = (showCorrectAnswer, pressedKeys = []) => {
    const combos =
      brassDict[selectedInstrument][notes[selectedInstrument][noteIndex]] || [];
    let correctKeys = [];
    if (combos.length > 0) {
      const first = combos[0];
      if (typeof first === 'string') {
        correctKeys = first.split('');
      } else if (Array.isArray(first)) {
        correctKeys = first.map((k) => String(k));
      } else {
        correctKeys = [];
      }
    }

    if (showCorrectAnswer) {
      showFeedback('incorrect', 2000);
    }

    setIsRunning(false);
    if (['trumpet', 'euphonium', 'tuba'].includes(selectedInstrument)) {
      if (showCorrectAnswer) {
        if (correctKeys.includes('j')) {
          setShowCorrectValve1(1);
        } else {
          setShowCorrectValve1(0);
        }
        if (correctKeys.includes('k')) {
          setShowCorrectValve2(1);
        } else {
          setShowCorrectValve2(0);
        }
        if (correctKeys.includes('l')) {
          setShowCorrectValve3(1);
        } else {
          setShowCorrectValve3(0);
        }
        if (correctKeys.includes(';')) {
          setShowCorrectValve4(1);
        } else {
          setShowCorrectValve4(0);
        }
      } else {
        setShowCorrectValve1(null);
        setShowCorrectValve2(null);
        setShowCorrectValve3(null);
        setShowCorrectValve4(null);
      }
    } else if (selectedInstrument === 'frenchHorn') {
      if (showCorrectAnswer) {
        if (correctKeys.includes('q')) {
          setShowCorrectValve1(1);
        } else {
          setShowCorrectValve1(0);
        }
        if (correctKeys.includes('w')) {
          setShowCorrectValve2(1);
        } else {
          setShowCorrectValve2(0);
        }
        if (correctKeys.includes('e')) {
          setShowCorrectValve3(1);
        } else {
          setShowCorrectValve3(0);
        }
        if (correctKeys.includes('c')) {
          setShowCorrectTrigger(1);
        } else {
          setShowCorrectTrigger(0);
        }
      } else {
        setShowCorrectValve1(null);
        setShowCorrectValve2(null);
        setShowCorrectValve3(null);
        setShowCorrectTrigger(null);
      }
    } else {
      if (correctKeys.includes('1')) {
        setCorrectSlideDistance(416);
      } else if (correctKeys.includes('2')) {
        setCorrectSlideDistance(346);
      } else if (correctKeys.includes('3')) {
        setCorrectSlideDistance(269);
      } else if (correctKeys.includes('4')) {
        setCorrectSlideDistance(199);
      } else if (correctKeys.includes('5')) {
        setCorrectSlideDistance(123);
      } else if (correctKeys.includes('6')) {
        setCorrectSlideDistance(48);
      } else if (correctKeys.includes('7')) {
        setCorrectSlideDistance(-25);
      }
      setShowCorrectSlide(showCorrectAnswer);
    }
  };

  const handleAnswer = (isCorrect) => {
    const currentNote = notes[selectedInstrument][noteIndex];

    if (isCorrect) {
      showFeedback('correct');

      const safeNote = currentNote.replace('#', 's');
      const sound = new Audio(`/sounds/${selectedInstrument}/${safeNote}.mp3`);
      sound.volume = volume / 100;
      sound.load();
      sound.play();

      setScore((prev) => prev + 1);
      if (score >= 99) {
        setSeconds(2);
      } else if (score >= 74) {
        setSeconds(3);
      } else if (score >= 49) {
        setSeconds(5);
      } else if (score >= 29) {
        setSeconds(10);
      } else if (score >= 14) {
        setSeconds(15);
      } else if (score >= 4) {
        setSeconds(20);
      }
    } else {
      setLives((prev) => prev - 1);
    }
    resetTimer();
    changeNote();
  };

  const toggleOptions = () => {
    setShowOptions((prev) => {
      const newVal = !prev;
      if (prev === true && newVal === false) {
        const opts = loadOptions();
        setSelectedInstrument(opts.selectedInstrument);
        setSlider1Value(opts.slider1Value);
        setSlider2Value(opts.slider2Value);
        setKeyLetter(opts.keyLetter);
        setAccidental(opts.accidental);
        setMode(opts.mode);
        setVolume(opts.volume);
      }
      return newVal;
    });
  };

  const tour = new Shepherd.Tour({
    useModalOverlay: true,
    defaultStepOptions: {
      exitOnEsc: true,
    },
  });
  if (selectedInstrument === 'trumpet' || selectedInstrument === 'euphonium') {
    tour.addStep({
      id: 'step-1',
      text: 'Use J, K, and L to press down the valves, and space bar for open fingerings.',
      attachTo: {
        element: '.instrument',
        on: 'bottom',
      },
      buttons: [
        {
          text: 'Finish',
          action: tour.complete,
        },
      ],
    });
  } else if (selectedInstrument === 'frenchHorn') {
    tour.addStep({
      id: 'step-1',
      text: 'Use Q, W, E, and C to press down the valves and trigger, and space bar for open fingerings.',
      attachTo: {
        element: '.instrument',
        on: 'top',
      },
      buttons: [
        {
          text: 'Finish',
          action: tour.complete,
        },
      ],
    });
  } else if (selectedInstrument === 'tuba') {
    tour.addStep({
      id: 'step-1',
      text: 'Use J, K, L, and ; to press down the valves, and space bar for open fingerings.',
      attachTo: {
        element: '.instrument',
        on: 'bottom',
      },
      buttons: [
        {
          text: 'Finish',
          action: tour.complete,
        },
      ],
    });
  } else {
    tour.addStep({
      id: 'step-1',
      text: 'Use the numbers 2-7 to change the slide position, and space bar for first position.',
      attachTo: {
        element: '.instrument',
        on: 'bottom',
      },
      buttons: [
        {
          text: 'Finish',
          action: tour.complete,
        },
      ],
    });
  }

  const toggleHelp = () => {
    tour.start();
  };

  const handleLoading = (loading) => {
    setIsLoading(loading);
  };

  return (
    <div>
      {isLoading && <Loading />}
      <div
        className={`${Styles.pageWrapper} ${
          showOptions ? Styles.darkerBackground : ''
        }`}
        style={{
          opacity: isLoading ? 0 : 1,
        }}
      >
        <div className={Styles.header}>
          <div className={Styles.subButtonsContainer}>
            <BackButton destination={'classroom'} />
            <button className={Styles.options} onClick={toggleOptions}>
              <img src={optionsButtonImage} alt="Options button" />
            </button>
            <button
              className={Styles.help}
              onClick={toggleHelp}
              onKeyDown={(e) => {
                if (e.key === ' ') {
                  e.preventDefault();
                }
              }}
            >
              <img src={HelpButtonImage} alt="Help button" />
            </button>
          </div>
        </div>
        <div className={Styles.classicWrapper}>
          <div className={Styles.container}>
            <div className={Styles.subContainer}>
              <div className={Styles.score}>
                <h3>Score:</h3>
                <h2>{score}</h2>
              </div>
              <div className={Styles.circle}>{timer}</div>
            </div>
          </div>
          <div className={Styles.musicDisplay}>
            <div
              className={`${Styles.feedbackOverlay} ${
                feedbackType
                  ? feedbackType === 'correct'
                    ? Styles.correct
                    : Styles.incorrect
                  : ''
              } ${feedbackType ? Styles.show : ''}`}
            />
            <div className={Styles.heartsContainer}>
              {[...Array(3).keys()].map((index) => (
                <img
                  key={index}
                  src={lives > index ? Heart : EmptyHeart}
                  alt={'Heart'}
                  style={{ width: '36px', marginLeft: '3px' }}
                />
              ))}
            </div>
            <div className={Styles.musicNote}>
              <MusicNote
                note={notes[selectedInstrument][noteIndex]}
                selectedInstrument={selectedInstrument}
              />
            </div>
            <div className={`${Styles.instrument} instrument`}>
              <Instrument
                correctKeys={
                  brassDict[selectedInstrument][
                    notes[selectedInstrument][noteIndex]
                  ]
                }
                handleIncorrect={handleIncorrect}
                handleAnswer={handleAnswer}
                instrument={selectedInstrument}
                handleLoading={handleLoading}
                isTimeUp={isTimeUp}
                setIsTimeUp={setIsTimeUp}
                setIsRunning={setIsRunning}
                mode={'Classic'}
              />
            </div>
          </div>
        </div>
        <div>
          {selectedInstrument === 'trombone' ? (
            <div>
              {showCorrectSlide ? (
                <div
                  className={Styles.slide}
                  style={{ marginRight: `${correctSlideDistance}px` }}
                >
                  <img src={CorrectSlide} alt={'Correct slide'} />
                  <div className={Styles.slideNumberContainer}>
                    <p>{correctSlideNumber}</p>
                  </div>
                </div>
              ) : (
                <></>
              )}
            </div>
          ) : selectedInstrument === 'tuba' ? (
            <div className={Styles.rotaryValves}>
              {showCorrectValve1 === 1 ? (
                <img src={CorrectValve} alt={'Show correct valve'} />
              ) : showCorrectValve1 === 0 ? (
                <img src={IncorrectValve} alt={'Show incorrect valve'} />
              ) : (
                <img
                  className={Styles.emptyValve}
                  src={EmptyCorrectValve}
                  alt={'Empty correct valve'}
                />
              )}
              {showCorrectValve2 === 1 ? (
                <img src={CorrectValve} alt={'Show correct valve'} />
              ) : showCorrectValve2 === 0 ? (
                <img src={IncorrectValve} alt={'Show incorrect valve'} />
              ) : (
                <img
                  className={Styles.emptyValve}
                  src={EmptyCorrectValve}
                  alt={'Empty correct valve'}
                />
              )}
              {showCorrectValve3 === 1 ? (
                <img src={CorrectValve} alt={'Show correct valve'} />
              ) : showCorrectValve3 === 0 ? (
                <img src={IncorrectValve} alt={'Show incorrect valve'} />
              ) : (
                <img
                  className={Styles.emptyValve}
                  src={EmptyCorrectValve}
                  alt={'Empty correct valve'}
                />
              )}
              {showCorrectValve4 === 1 ? (
                <img src={CorrectValve} alt={'Show correct valve'} />
              ) : showCorrectValve4 === 0 ? (
                <img src={IncorrectValve} alt={'Show incorrect valve'} />
              ) : (
                <img
                  className={Styles.emptyValve}
                  src={EmptyCorrectValve}
                  alt={'Empty correct valve'}
                />
              )}
            </div>
          ) : (
            <div
              className={
                selectedInstrument === 'frenchHorn'
                  ? Styles.rotaryValves
                  : Styles.valves
              }
            >
              {showCorrectValve1 === 1 ? (
                <img src={CorrectValve} alt={'Show correct valve'} />
              ) : showCorrectValve1 === 0 ? (
                <img src={IncorrectValve} alt={'Show incorrect valve'} />
              ) : (
                <img
                  className={Styles.emptyValve}
                  src={EmptyCorrectValve}
                  alt={'Empty correct valve'}
                />
              )}
              {showCorrectValve2 === 1 ? (
                <img src={CorrectValve} alt={'Show correct valve'} />
              ) : showCorrectValve2 === 0 ? (
                <img src={IncorrectValve} alt={'Show incorrect valve'} />
              ) : (
                <img
                  className={Styles.emptyValve}
                  src={EmptyCorrectValve}
                  alt={'Empty correct valve'}
                />
              )}
              {showCorrectValve3 === 1 ? (
                <img src={CorrectValve} alt={'Show correct valve'} />
              ) : showCorrectValve3 === 0 ? (
                <img src={IncorrectValve} alt={'Show incorrect valve'} />
              ) : (
                <img
                  className={Styles.emptyValve}
                  src={EmptyCorrectValve}
                  alt={'Empty correct valve'}
                />
              )}
              {showCorrectTrigger === 1 ? (
                <img
                  className={Styles.trigger}
                  src={CorrectValve}
                  alt={'Show correct trigger'}
                />
              ) : showCorrectTrigger === 0 ? (
                <img
                  className={Styles.trigger}
                  src={IncorrectValve}
                  alt={'Show incorrect trigger'}
                />
              ) : (
                <img
                  className={Styles.emptyTrigger}
                  src={EmptyCorrectValve}
                  alt={'Empty correct trigger'}
                />
              )}
            </div>
          )}
        </div>
        {showOptions && <Options toggleOptions={toggleOptions} />}
      </div>
    </div>
  );
};

export default Classic;